import { useMemo } from 'react'

import { VAULT_CONFIGS } from '../strategies/config/vaultConfigs'
import { CharmVaultStrategy, IslandVaultStrategy, KXRouterStrategy, V2VaultStrategy } from '../strategies/vault'

export function useVaultStrategy(
  vaultType: 'island' | 'charmvault' | 'v2',
  dependencies: any,
  useSingleSided?: boolean
) {
  return useMemo(() => {
    const config = VAULT_CONFIGS[vaultType]

    // Create the base strategy first
    let baseStrategy
    switch (vaultType) {
      case 'island':
        if (
          !dependencies.wagmiPublicClient ||
          !dependencies.writeContractAsync ||
          !dependencies.smartIslandRouterAddress
        ) {
          throw new Error(
            'wagmiPublicClient, writeContractAsync, and smartIslandRouter.address are required for IslandVaultStrategy'
          )
        }
        baseStrategy = new IslandVaultStrategy(
          dependencies.smartIslandRouterAddress,
          dependencies.wagmiPublicClient,
          dependencies.writeContractAsync,
          dependencies.chainId
        )
        break

      case 'charmvault':
        if (!dependencies.wagmiPublicClient || !dependencies.writeContractAsync || !dependencies.charmVaultAddress) {
          throw new Error(
            'wagmiPublicClient, writeContractAsync, and charmVault.address are required for CharmVaultStrategy'
          )
        }
        baseStrategy = new CharmVaultStrategy(
          dependencies.charmVaultAddress,
          dependencies.wagmiPublicClient,
          dependencies.writeContractAsync,
          dependencies.chainId
        )
        break

      case 'v2':
        if (!dependencies.wagmiPublicClient || !dependencies.writeContractAsync || !dependencies.v2RouterAddress) {
          throw new Error(
            'wagmiPublicClient, writeContractAsync, and v2Router.address are required for V2VaultStrategy'
          )
        }
        baseStrategy = new V2VaultStrategy(
          dependencies.v2RouterAddress,
          dependencies.wagmiPublicClient,
          dependencies.writeContractAsync,
          dependencies.chainId
        )
        break

      default:
        throw new Error(`Unsupported vault type: ${vaultType}`)
    }

    // If single-sided deposits are being used, wrap with KXRouterStrategy
    if (useSingleSided) {
      if (!dependencies.wagmiPublicClient || !dependencies.sendTransactionAsync) {
        throw new Error('wagmiPublicClient and sendTransactionAsync are required for KXRouterStrategy')
      }
      return {
        strategy: new KXRouterStrategy(
          baseStrategy,
          vaultType,
          useSingleSided,
          dependencies.wagmiPublicClient,
          dependencies.sendTransactionAsync,
          dependencies.chainId
        ),
        config,
      }
    }

    // Otherwise return the base strategy
    return {
      strategy: baseStrategy,
      config,
    }
  }, [vaultType, dependencies, useSingleSided])
}
